import { useContext, useEffect, useMemo, useState } from "react";
import AddButtonWithArrow from "../../components/firstTimeUserExperience/commonCom/AddButtonWithArrow";
import RadioButton from "../../components/common/RadioButton";
import { GeneratePdfContext } from "../../utils/context/GeneratePdfContext";
import apiHelper from "../../utils/apiHelper";
import {
  PropertyService,
  FilterEntityEnum,
  UnitService,
} from "@propertelligent/client-api";
import { UserLoginData } from "../../components/common/charts/Enums";
import MultiselectDropdown from "../../components/common/MultiselectDropdown";
import { Form } from "react-bootstrap";
import AddTraspButton from "../../components/firstTimeUserExperience/commonCom/AddTraspButton";
import Search from "../../components/common/search";
import SimpleButton from "../buttons/Simple-button";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { sortOptionsAlphabeticallyOtherLast } from "../../utils/helper";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SelectedCategories = { [key: string]: string[] };

interface FilterDropdownProps {
  filters?: { [key: string]: string[] };
  selectedCategories: SelectedCategories;
  handleCategoryChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    column: string
  ) => void;
  propertyResponse?: () => void;
  unitResponse?: () => void;
  setShow: (show: boolean) => void;
  resetFilters?: () => void;
  propertyResponseTX?: () => void;
  organizationsTXResponse?: () => void;
  
  onApplyFilter: (
    currentOption: FilterEntityEnum,
    currentIds: number | number[],
    currentCategories: SelectedCategories
  ) => void;
  /** @deprecated kept for backward compat — use onApplyFilter */
  fetchData?: (page: number) => void;
  selectedView?: { id: number; img: string; name: string; linkpath?: string };
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  isContractor?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FilterDropdown = ({
  filters = {},
  selectedCategories,
  handleCategoryChange,
  propertyResponse = () => {},
  unitResponse = () => {},
  setShow,
  resetFilters = () => {},
  propertyResponseTX = () => {},
  organizationsTXResponse = () => {},
  onApplyFilter,
  selectedView,
  isEntity,
  entityId,
  entityTypeId,
  isContractor,
}: FilterDropdownProps) => {
  const {
    selectedOption,
    setSelectedOption,
    selectedValues,
    setSelectedValues,
    setSelectedIds,
    selectedIds,
    multiSelectOptions,
    setMultiSelectOptions,
  } = useContext(GeneratePdfContext);

  const [filterPayer, setFilterPayer] = useState("");
  const [filterPayee, setFilterPayee] = useState("");

  const organizationId = parseInt(
    sessionStorage.getItem(UserLoginData.organization_id)
  );

  // ── Clean up context when this dropdown unmounts ───────────────────────────
  useEffect(() => {
    return () => {
      setSelectedIds([]);
      setSelectedValues([]);
      setMultiSelectOptions([]);
      setSelectedOption(FilterEntityEnum.Organization);
    };
    
  }, []);

  // ── Pre-populate selections when used in entity (property/unit) context ───
  useEffect(() => {
    if (!isEntity || !entityId || !entityTypeId) return;

    if (entityTypeId === FilterEntityEnum.Property) {
      setSelectedOption(FilterEntityEnum.Property);
      setSelectedIds([entityId]);

      // Single lookup call — populate both the multiselect options list AND
      // the display name chip for the selected entity.
      apiHelper(
        PropertyService.getApiPropertyOrganizationlookup,
        { showNotification: false },
        organizationId
      ).then((res) => {
        const list = res?.data?.success ?? [];
        setMultiSelectOptions(list);
        const match = list.find((p: any) => p.id === entityId);
        if (match?.name) setSelectedValues([match.name]);
      });

    } else if (entityTypeId === FilterEntityEnum.Unit) {
      setSelectedOption(FilterEntityEnum.Unit);
      setSelectedIds([entityId]);

      // Single lookup call — same consolidation as above.
      apiHelper(
        UnitService.getApiUnitOrganizationlookup,
        { showNotification: false },
        organizationId
      ).then((res) => {
        const list = res?.data?.success ?? [];
        setMultiSelectOptions(list);
        const match = list.find((u: any) => u.id === entityId);
        if (match?.name) setSelectedValues([match.name]);
      });
    }
    
  }, [isEntity, entityId, entityTypeId]);

  // ── Load property / unit options when radio changes ───────────────────────
  const loadOptions = (option: FilterEntityEnum) => {
    if (option === FilterEntityEnum.Property) {
      apiHelper(
        PropertyService.getApiPropertyOrganizationlookup,
        { showNotification: false },
        organizationId
      )
        .then((res) => setMultiSelectOptions(res?.data?.success ?? []))
        .catch(console.error);
    } else if (option === FilterEntityEnum.Unit) {
      setMultiSelectOptions([]);
      apiHelper(
        UnitService.getApiUnitOrganizationlookup,
        { showNotification: false },
        organizationId
      )
        .then((res) => setMultiSelectOptions(res?.data?.success ?? []))
        .catch(console.error);
    } else {
      setMultiSelectOptions([]);
    }
  };

  useEffect(() => {
    if (!isEntity) loadOptions(selectedOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  // ── Radio handler ─────────────────────────────────────────────────────────
  const radioOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(Number(e.target.value) as FilterEntityEnum); // string → number fix
    setSelectedIds([]);
    setSelectedValues([]);
    setMultiSelectOptions([]);
  };

  const radioOptions = [
    {
      label: "Organization",
      value: FilterEntityEnum.Organization,
      checked: selectedOption == FilterEntityEnum.Organization,
    },
    {
      label: "Properties",
      value: FilterEntityEnum.Property,
      checked: selectedOption == FilterEntityEnum.Property,
    },
    {
      label: "Units",
      value: FilterEntityEnum.Unit,
      checked: selectedOption == FilterEntityEnum.Unit,
    },
  ];

  // ── Multi-select checkbox handler ─────────────────────────────────────────
  const handleCheckbox = (value: string, id: number) => {
    setSelectedValues((prev: string[]) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
    );
    setSelectedIds((prev: number[]) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // ── Apply filter ──────────────────────────────────────────────────────────
  const handleFilterApply = () => {
    const isPropertySelected = selectedOption == FilterEntityEnum.Property;
    const isUnitSelected = selectedOption == FilterEntityEnum.Unit;

    if (
      (isPropertySelected || isUnitSelected) &&
      (!selectedIds || selectedIds.length === 0)
    ) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: `Please select at least one ${isPropertySelected ? "property" : "unit"}`,
        type: NotificationType.Failure,
      });
      return;
    }

    setShow(false);
    // Pass current context values to the parent so it can build filters with
    // fresh data — no stale closures.
    onApplyFilter(selectedOption, selectedIds, selectedCategories);
  };

  // ── Derived filter lists ───────────────────────────────────────────────────
  const fromItems = useMemo(
    () => sortOptionsAlphabeticallyOtherLast(filters["From"] ?? [], (item: string) => item),
    [filters],
  );
  const toItems = useMemo(
    () => sortOptionsAlphabeticallyOtherLast(filters["To"] ?? [], (item: string) => item),
    [filters],
  );
  const categories = useMemo(
    () => sortOptionsAlphabeticallyOtherLast(filters["Category"] ?? [], (item: string) => item),
    [filters],
  );
  const paymentMethod = useMemo(
    () => sortOptionsAlphabeticallyOtherLast(filters["PaymentMethod"] ?? [], (item: string) => item),
    [filters],
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="filter-component">
      <div className="BorderBottom">
        <h4 className="heading px-3 dynamic-text-black">Filter by</h4>
      </div>

      <div className="d-flex">
        {/* View-by (org / property / unit) radio section */}
        {!isEntity && (
          <div className="filterRow">
            <div className="transaction-filter-header">
              <h5 className="normal_text fw-500 p-3 sticky-header black BorderRight">
                View By
              </h5>
              <div className="viewByRow pe-3">
                <div className="d-flex flex-column gap-3 ps-3">
                  {radioOptions.map((option, index) => (
                    <label key={index} className="custom-radio-label">
                      <input
                        type="radio"
                        value={option.value}
                        checked={option.checked}
                        onChange={radioOnChangeHandler}
                        name="viewBy"
                        className="custom-radio-input"
                      />
                      <span className="custom-radio-circle"></span>
                      <span className="custom-radio-text">{option.label}</span>
                    </label>
                  ))}
                </div>

                {Number(selectedOption) !== FilterEntityEnum.Organization && (
                  <Form.Label>
                    <MultiselectDropdown
                      options={multiSelectOptions}
                      selectedOption={selectedOption}
                      selectedIds={selectedIds}
                      placeholder="Select an option"
                      onChange={handleCheckbox}
                      width="240px"
                    />
                  </Form.Label>
                )}

                <div className="selectedData">
                  {selectedValues?.map((value: string, i: number) => (
                    <p key={i} className="selectedValueLabel normal_text">
                      {value}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category / payment method / payee / payer columns */}
        <div
          className="BorderLeft"
          style={{
            overflow: "auto",
            width: "100%",
            display: "flex",
            overflowY: "hidden",
          }}
        >
          {/* Category */}
          <div className="transaction-filter-header">
            <h5 className="normal_text fw-500 sticky-header p-3 black">
              Categories
            </h5>
            <div className="filter-column">
              {categories.map((item, index) => (
                <div key={index} className="filter-row">
                  <RadioButton
                    type="checkbox"
                    value={item}
                    name="TranactionCreditDebitTypeId"
                    checked={selectedCategories[
                      "TranactionCreditDebitTypeId"
                    ]?.includes(item)}
                    onChange={(e) =>
                      handleCategoryChange(e, "TranactionCreditDebitTypeId")
                    }
                    label={item}
                    checkBoxClass="sub_text filterCheckbox"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Payment method */}
          <div className="BorderLeft transaction-filter-header">
            <h5 className="normal_text fw-500 sticky-header p-3 black">
              Payment method
            </h5>
            <div className="filter-column">
              {paymentMethod.map((item, index) => (
                <div key={index} className="filter-row">
                  <RadioButton
                    name="paymentMethod.PaymentMethodTypeId"
                    type="checkbox"
                    value={item}
                    checked={selectedCategories[
                      "paymentMethod.PaymentMethodTypeId"
                    ]?.includes(item)}
                    onChange={(e) =>
                      handleCategoryChange(
                        e,
                        "paymentMethod.PaymentMethodTypeId"
                      )
                    }
                    label={item}
                    checkBoxClass="sub_text filterCheckbox"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Payee + Payer (hidden for contractor) */}
          {!isContractor && (
            <>
              <div
                className="BorderLeft transaction-filter-header"
                id="payee-column"
              >
                <h5 className="normal_text fw-500 sticky-header black p-3">
                  Payee
                </h5>
                <div className="mb-3">
                  <Search
                    placeholderName="Search Payee"
                    searchValue={filterPayee}
                    searchFilterChange={(value: string) =>
                      setFilterPayee(value)
                    }
                  />
                </div>
                <div className="filter-column">
                  {toItems
                    .filter((item) =>
                      item.toLowerCase().includes(filterPayee.toLowerCase())
                    )
                    .map((item, index) => (
                      <div key={index} className="filter-row">
                        <RadioButton
                          name="payeeAccount.accountName"
                          type="checkbox"
                          value={item}
                          checked={selectedCategories[
                            "payeeAccount.accountName"
                          ]?.includes(item)}
                          onChange={(e) =>
                            handleCategoryChange(e, "payeeAccount.accountName")
                          }
                          label={item}
                          checkBoxClass="sub_text filterCheckbox"
                        />
                      </div>
                    ))}
                </div>
              </div>

              <div
                className="BorderLeft transaction-filter-header"
                id="payer-column"
              >
                <h5 className="normal_text fw-500 sticky-header black p-3">
                  Payer
                </h5>
                <div className="mb-3">
                  <Search
                    placeholderName="Search Payer"
                    searchValue={filterPayer}
                    searchFilterChange={(value: string) =>
                      setFilterPayer(value)
                    }
                  />
                </div>
                <div className="filter-column">
                  {fromItems
                    .filter((item) =>
                      item.toLowerCase().includes(filterPayer.toLowerCase())
                    )
                    .map((item, index) => (
                      <div key={index} className="filter-row">
                        <RadioButton
                          name="payeeAccount.accountName"
                          type="checkbox"
                          value={item}
                          checked={selectedCategories[
                            "payeeAccount.accountName"
                          ]?.includes(item)}
                          onChange={(e) =>
                            handleCategoryChange(e, "payeeAccount.accountName")
                          }
                          label={item}
                          checkBoxClass="sub_text filterCheckbox"
                        />
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer buttons */}
      <div className="BorderTop applyFilterBtn">
        <AddTraspButton
          bname="Clear filter"
          onClick={resetFilters}
          width="fit-content"
        />
        <SimpleButton label="Apply filter" onClick={handleFilterApply} />
      </div>
    </div>
  );
};

export default FilterDropdown;